import { Archive, ArchiveRestore, Edit2, Plus, Power, Save, Trash2 } from 'lucide-react';
import ENTITY_ICONS from '../components/common/entityIcons';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import DataTable, { RowAction, RowActions } from '../components/common/DataTable';
import StatusBadge from '../components/common/StatusBadge';
import { STATUS_OPTIONS } from '../components/common/filters';
import ErrorState from '../components/common/ErrorState';
import Modal from '../components/common/Modal';
import PageHeader from '../components/common/PageHeader';
import SkeletonList from '../components/common/SkeletonList';
import { useApps } from '../contexts/AppsContext';
import useServerTable from '../hooks/useServerTable';
import useTenants from '../hooks/useTenants';
import componentsService from '../services/components';
import rolesService from '../services/roles';
import { useNotice } from '../components/common/Notices';
import { useConfirm } from '../components/common/ConfirmDialog';
import { Button } from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';
import { Field } from '../components/ui/Field';
import { Select, TextInput, Textarea } from '../components/ui/Input';

const SCOPE_OPTIONS = [
    { value: 'application', label: 'App-level' },
    { value: 'tenant', label: 'Tenant' },
];

// role_detail is JSON; the driver may hand it over parsed or as text.
const detailText = (detail) => {
    if (detail === null || detail === undefined || detail === '') return '';
    return typeof detail === 'string' ? detail : JSON.stringify(detail);
};

const RolesPage = () => {
    const { loading: loadingApps, error: appsError, selectedAppId: appId } = useApps();
    const notice = useNotice();
    const confirm = useConfirm();
    const [error, setError] = useState('');
    const [searchParams] = useSearchParams();
    // Archive is per tenant (AIQ-463): with a tenant chosen the list shows that tenant's
    // view — its own roles plus application roles, each marked archived or not.
    const [tenantId, setTenantId] = useState(() => searchParams.get('tenant_id') || '');
    const tenantsQuery = useTenants(appId);

    // Modals
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showRoleComponentsModal, setShowRoleComponentsModal] = useState(false);

    // Selection
    const [selectedRole, setSelectedRole] = useState(null);

    // Component Mapping State
    const [roleComponents, setRoleComponents] = useState([]);
    const [allComponents, setAllComponents] = useState([]);
    const [loadingRoleComponents, setLoadingRoleComponents] = useState(false);
    const [mappingError, setMappingError] = useState('');

    // Add/Edit Mapping Form
    const [newMapping, setNewMapping] = useState({ component_name: '', access: '{"access": "readonly"}' });
    const [editingMappingId, setEditingMappingId] = useState(null);
    const [editAccessValue, setEditAccessValue] = useState('');

    const [formData, setFormData] = useState({
        rolename: '',
        role_detail: '{}',
    });

    // Flag roles (presentation 'FLAG') are checkboxes on the user form, never roles here;
    // the paged endpoint leaves them out.
    // Scope (app-level vs tenant role) only exists when a tenant is in play; the column and
    // its filter appear once the list shows scoped rows (or a link asks for it).
    const [scopeSeen, setScopeSeen] = useState(false);
    const showScope = scopeSeen || searchParams.has('scope');
    const [detailSeen, setDetailSeen] = useState(false);
    // `scope` is only on rows when a tenant is in play (tenant caller or ?tenant_id=).
    // URL keys: status, scope, users_min/users_max.
    const columns = [
        {
            key: 'role_name',
            header: 'Role',
            sortKey: 'name',
            locked: true,
            render: (role) => <span className="font-medium text-slate-900">{role.role_name}</span>,
        },
        ...(showScope
            ? [
                  {
                      key: 'scope',
                      header: 'Scope',
                      sortKey: 'scope',
                      render: (role) => (role.scope === 'tenant' ? 'Tenant' : 'Application'),
                      filter: { type: 'multiselect', options: SCOPE_OPTIONS, defaultExpanded: true },
                  },
              ]
            : []),
        // Detail is optional JSON; the column appears once a role in this app has any.
        ...(!detailSeen
            ? []
            : [
                  {
                      key: 'role_detail',
                      header: 'Detail',
                      render: (role) => {
                          const text = detailText(role.role_detail);
                          return text ? (
                              <code className="block max-w-xs truncate text-xs text-slate-600 font-mono" title={text}>
                                  {text}
                              </code>
                          ) : (
                              '—'
                          );
                      },
                  },
              ]),
        {
            // Members holding the role (active assignment, active app membership); the link
            // opens the Users list filtered to exactly those rows.
            key: 'user_count',
            header: 'Users',
            sortKey: 'users',
            align: 'right',
            filter: { key: 'users', type: 'number' },
            render: (role) => {
                const n = Number(role.user_count) || 0;
                if (n === 0) return <span className="tabular-nums text-slate-500">0</span>;
                const to = `/users?app_id=${encodeURIComponent(appId.trim())}&role=${encodeURIComponent(role.role_id)}`;
                return (
                    <Link
                        to={to}
                        aria-label={`View ${n} ${n === 1 ? 'user' : 'users'} with role ${role.role_name}`}
                        className="rounded font-medium tabular-nums text-primary-700 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200"
                    >
                        {n}
                    </Link>
                );
            },
        },
        {
            key: 'status',
            header: 'Status',
            sortKey: 'status',
            // Archived is shown beside the role's own status, never instead of it.
            render: (role) => (
                <span className="inline-flex flex-wrap gap-1">
                    <StatusBadge active={Boolean(role.is_active)} />
                    {role.archived && <StatusBadge status="archived" />}
                </span>
            ),
            filter: { type: 'multiselect', options: STATUS_OPTIONS, defaultExpanded: true },
        },
        {
            key: 'actions',
            header: <span className="sr-only">Actions</span>,
            label: 'Row actions',
            locked: true,
            align: 'right',
            render: (role) => (
                <RowActions>
                    <RowAction
                        icon={ENTITY_ICONS.components}
                        label="Component access"
                        tone="info"
                        onClick={() => openRoleComponents(role)}
                    />
                    <RowAction
                        icon={Power}
                        label={role.is_active ? 'Deactivate role' : 'Activate role'}
                        tone={role.is_active ? 'warning' : 'success'}
                        onClick={() => openManageModal(role)}
                    />
                    <RowAction icon={Trash2} label="Delete role" tone="danger" onClick={() => handleDeleteRole(role)} />
                    {tenantId && !role.is_protected && (
                        <RowAction
                            icon={role.archived ? ArchiveRestore : Archive}
                            label={role.archived ? 'Restore role' : 'Archive role'}
                            tone={role.archived ? 'success' : 'warning'}
                            onClick={() => handleArchiveRole(role, !role.archived)}
                        />
                    )}
                </RowActions>
            ),
        },
    ];

    const table = useServerTable(
        (query) => rolesService.getPage(appId.trim(), tenantId ? { ...query, tenant_id: tenantId } : query),
        {
            deps: [appId, tenantId],
            enabled: Boolean(appId && appId.trim()),
            defaultSort: { key: 'name', dir: 'asc' },
            columns,
        }
    );

    useEffect(() => {
        if (table.rows.some((r) => r.scope)) setScopeSeen(true);
        if (table.rows.some((r) => detailText(r.role_detail))) setDetailSeen(true);
    }, [table.rows]);

    // A role picked under the previous app means nothing in the new one.
    useEffect(() => {
        setSelectedRole(null);
        setShowDeleteModal(false);
        setShowRoleComponentsModal(false);
    }, [appId]);

    // A tenant of the previous app means nothing in the new one.
    const [tenantsForApp, setTenantsForApp] = useState(appId);
    if (tenantsForApp !== appId) {
        setTenantsForApp(appId);
        setTenantId('');
    }

    // Role CRUD -------------------------------------------------------------

    const handleCreateRole = async (e) => {
        e.preventDefault();
        if (!appId) return setError('App ID is required');
        setError('');
        try {
            await rolesService.addRole(appId, {
                rolename: formData.rolename,
                role_detail: JSON.parse(formData.role_detail || '{}'),
            });
            setShowCreateModal(false);
            resetForm();
            table.reload();
            notice.success(`Role ${formData.rolename} created.`);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to create role');
        }
    };

    const handleUpdateStatus = async () => {
        if (!appId || !selectedRole) return;
        setError('');
        try {
            await rolesService.updateStatus(appId, {
                rolename: selectedRole.role_name,
                is_active: selectedRole.is_active ? 0 : 1,
            });
            setShowDeleteModal(false);
            setSelectedRole(null);
            table.reload();
            notice.success(`Role ${selectedRole.role_name} ${selectedRole.is_active ? 'deactivated' : 'activated'}.`);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to update status');
        }
    };

    // From the row (its own confirmation) or from the Manage dialog (the selected role).
    const handleDeleteRole = async (role = selectedRole) => {
        if (!appId || !role) return;
        const fromDialog = showDeleteModal;
        const ok = await confirm({
            title: `Delete role ${role.role_name}?`,
            body: 'A role can’t be deleted while anyone still holds it. Reassign them first; deleting then removes the role and its access. This can’t be undone from the console.',
            confirmLabel: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;
        setError('');
        try {
            await rolesService.deleteRole(appId, { rolename: role.role_name });
            setShowDeleteModal(false);
            setSelectedRole(null);
            table.reload();
            notice.success(`Role ${role.role_name} deleted.`);
        } catch (err) {
            const message = err?.response?.data?.message || err?.message || 'Failed to delete role';
            if (fromDialog) setError(message);
            else notice.error(message);
        }
    };

    // Archive is per tenant: holders keep the assignment but lose the role's access in
    // that tenant, it can't be newly assigned there, and other tenants are unaffected.
    const handleArchiveRole = async (role, archive) => {
        const tenantName = tenantsQuery.byId[tenantId]?.tenant_name || tenantId;
        const ok = await confirm({
            title: `${archive ? 'Archive' : 'Restore'} role ${role.role_name} for ${tenantName}?`,
            body: archive
                ? 'Users in this tenant who hold it keep the assignment but lose its access, and it can’t be given to anyone new until it’s restored. Other tenants are not affected.'
                : 'Users in this tenant who still hold it get its access back when they next sign in or their session renews.',
            confirmLabel: archive ? 'Archive' : 'Restore',
            tone: archive ? 'danger' : 'default',
        });
        if (!ok) return;
        setError('');
        try {
            const payload = { role_id: role.role_id, tenant_id: tenantId };
            await (archive ? rolesService.archiveRole(appId, payload) : rolesService.restoreRole(appId, payload));
            table.reload();
            notice.success(`Role ${role.role_name} ${archive ? 'archived' : 'restored'} for ${tenantName}.`);
        } catch (err) {
            notice.error(err?.response?.data?.message || err?.message || 'Failed to update role');
        }
    };

    // Mapping CRUD ----------------------------------------------------------

    const openRoleComponents = async (role) => {
        if (!appId) return;
        setSelectedRole(role);
        setRoleComponents([]);
        setAllComponents([]);
        setLoadingRoleComponents(true);
        setShowRoleComponentsModal(true);
        setMappingError('');
        setNewMapping({ component_name: '', access: '{"access": "readonly"}' });
        setEditingMappingId(null);
        try {
            // Load Mappings
            const roleData = await rolesService.getRoleDetail(appId, role.role_name);
            const rawComps =
                Array.isArray(roleData) && roleData[0]?.component_details ? roleData[0].component_details : [];
            setRoleComponents(rawComps.filter((c) => c.component_id)); // Filter out nulls

            // Load Available Components for Dropdown
            const allComps = await componentsService.getAll(appId);
            setAllComponents(allComps || []);
        } catch (err) {
            setMappingError(err?.response?.data?.message || err?.message || 'Failed to load data');
        } finally {
            setLoadingRoleComponents(false);
        }
    };

    const refreshMappings = async () => {
        if (!selectedRole) return;
        try {
            const roleData = await rolesService.getRoleDetail(appId, selectedRole.role_name);
            const rawComps =
                Array.isArray(roleData) && roleData[0]?.component_details ? roleData[0].component_details : [];
            setRoleComponents(rawComps.filter((c) => c.component_id)); // Filter out nulls
        } catch (err) {
            setMappingError(err?.response?.data?.message || err?.message || 'Couldn’t refresh the component list.');
        }
    };

    const handleAddMapping = async (e) => {
        e.preventDefault();
        if (!newMapping.component_name) return setMappingError('Select a component');
        setMappingError('');
        try {
            await componentsService.addRoleMapping(appId, {
                rolename: selectedRole.role_name,
                component_name: newMapping.component_name,
                role_component_access: JSON.parse(newMapping.access),
            });
            setNewMapping({ ...newMapping, component_name: '' }); // Reset component selection
            await refreshMappings();
        } catch (err) {
            setMappingError(err?.response?.data?.message || err?.message || 'Failed to add mapping');
        }
    };

    const handleDeleteMapping = async (componentName) => {
        const ok = await confirm({
            title: `Remove ${componentName} from role ${selectedRole.role_name}?`,
            body: 'Users with this role lose access to the component. The component itself is not deleted.',
            confirmLabel: 'Remove',
            tone: 'danger',
        });
        if (!ok) return;
        setMappingError('');
        try {
            await componentsService.removeRoleMapping(appId, {
                rolename: selectedRole.role_name,
                component_name: componentName,
            });
            await refreshMappings();
        } catch (err) {
            setMappingError(err?.response?.data?.message || err?.message || 'Failed to remove mapping');
        }
    };

    const startEditing = (comp) => {
        setEditingMappingId(comp.component_id);
        const accessVal = typeof comp.access === 'string' ? comp.access : JSON.stringify(comp.access || {});
        setEditAccessValue(accessVal);
    };

    const handleUpdateMapping = async (componentName) => {
        setMappingError('');
        try {
            await componentsService.updateRoleAccess(appId, {
                rolename: selectedRole.role_name,
                component_name: componentName,
                updated_access: JSON.parse(editAccessValue),
            });
            setEditingMappingId(null);
            await refreshMappings();
        } catch (err) {
            setMappingError(err?.response?.data?.message || err?.message || 'Failed to update access');
        }
    };

    const resetForm = () => {
        setFormData({ rolename: '', role_detail: '{}' });
    };

    // Status and delete share one "Manage" modal, as before.
    const openManageModal = (role) => {
        setError('');
        setSelectedRole(role);
        setShowDeleteModal(true);
    };

    // Helper to filter out already mapped components
    const availableComponents = allComponents.filter(
        (c) => !roleComponents.some((rc) => rc.component_name === c.component_name)
    );

    // `scope` is only on rows when a tenant is in play (tenant caller or ?tenant_id=).


    return (
        <div className="space-y-4">
            <PageHeader
                title="Roles"
                actions={
                    appId && (
                        <>
                            {tenantsQuery.items.length > 0 && (
                                // Select is full width: size its container so it shares a row with the button.
                                <div className="w-52 max-w-full">
                                    <Select
                                        aria-label="Tenant"
                                        value={tenantId}
                                        onChange={(e) => setTenantId(e.target.value)}
                                    >
                                        <option value="">All tenants (app-level)</option>
                                        {tenantsQuery.items.map((t) => (
                                            <option key={t.tenant_id} value={t.tenant_id}>
                                                {t.tenant_name || t.tenant_id}
                                            </option>
                                        ))}
                                    </Select>
                                </div>
                            )}
                            <Button
                                onClick={() => {
                                    resetForm();
                                    setError('');
                                    setShowCreateModal(true);
                                }}
                            >
                                <Plus className="h-4 w-4" aria-hidden="true" /> Add Role
                            </Button>
                        </>
                    )
                }
            />

            {loadingApps ? (
                <SkeletonList />
            ) : appsError ? (
                <ErrorState message={appsError} />
            ) : !appId ? (
                <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
                    <p className="text-slate-600">Choose an app in the sidebar to see its roles.</p>
                </div>
            ) : (
                <DataTable
                    {...table.tableProps}
                    columns={columns}
                    tableKey="roles"
                    rowKey="role_id"
                    searchPlaceholder="Search role name…"
                    emptyMessage="No roles found for the selected app."
                />
            )}

            {/* Create Role Modal */}
            <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Create New Role" size="md">
                <form onSubmit={handleCreateRole} className="space-y-4">
                    <Field label="Role Name *">
                        <TextInput
                            type="text"
                            required
                            value={formData.rolename}
                            onChange={(e) => setFormData({ ...formData, rolename: e.target.value })}
                            placeholder="Role Name"
                        />
                    </Field>
                    <Field label="Role Detail (JSON) *">
                        <Textarea
                            required
                            rows={4}
                            value={formData.role_detail}
                            onChange={(e) => setFormData({ ...formData, role_detail: e.target.value })}
                            className="font-mono"
                            placeholder='{"access": "read"}'
                        />
                    </Field>

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    <div className="flex gap-3 pt-4 border-t border-slate-200">
                        <button
                            type="submit"
                            className="flex-1 rounded-lg bg-primary-600 text-white font-medium py-2.5 hover:bg-primary-700"
                        >
                            Create Role
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowCreateModal(false)}
                            className="flex-1 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Status/Delete Modal */}
            <Modal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setSelectedRole(null);
                }}
                title={selectedRole ? `Manage ${selectedRole.role_name}` : 'Manage Role'}
                size="sm"
            >
                <div className="space-y-4">
                    {selectedRole && (
                        <div className="space-y-3">
                            <div>
                                <p className="text-sm text-slate-600">Current Status:</p>
                                <p
                                    className={`text-sm font-medium ${
                                        selectedRole.is_active ? 'text-emerald-700' : 'text-amber-700'
                                    }`}
                                >
                                    {selectedRole.is_active ? 'Active' : 'Inactive'}
                                </p>
                            </div>
                            <div className="flex gap-3">
                                <button
                                    onClick={handleUpdateStatus}
                                    className={`flex-1 rounded-lg font-medium py-2.5 ${
                                        selectedRole.is_active
                                            ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
                                            : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
                                    }`}
                                >
                                    {selectedRole.is_active ? 'Deactivate role' : 'Activate role'}
                                </button>
                                <button
                                    onClick={() => handleDeleteRole()}
                                    className="flex-1 rounded-lg bg-red-50 text-red-700 font-medium py-2.5 hover:bg-red-100 border border-red-200"
                                >
                                    Delete Role
                                </button>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    <div className="pt-4 border-t border-slate-200">
                        <button
                            onClick={() => {
                                setShowDeleteModal(false);
                                setSelectedRole(null);
                            }}
                            className="w-full rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Role Components Modal (Mappings) */}
            <Modal
                isOpen={showRoleComponentsModal}
                onClose={() => {
                    setShowRoleComponentsModal(false);
                    setSelectedRole(null);
                    setRoleComponents([]);
                    setMappingError('');
                }}
                title={selectedRole ? `Manage Access for ${selectedRole.role_name}` : 'Component Access'}
                size="lg"
            >
                <div className="space-y-6">
                    {/* Add New Mapping Form */}
                    <form
                        onSubmit={handleAddMapping}
                        className="p-4 bg-slate-50 rounded-xl border border-slate-100 space-y-3"
                    >
                        <h4 className="text-sm font-semibold text-slate-800">Assign New Component</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <select
                                className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                                value={newMapping.component_name}
                                onChange={(e) => setNewMapping({ ...newMapping, component_name: e.target.value })}
                                required
                            >
                                <option value="">Select Component...</option>
                                {availableComponents.map((c) => (
                                    <option key={c.component_id} value={c.component_name}>
                                        {c.component_name}
                                    </option>
                                ))}
                            </select>
                            <input
                                className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono"
                                value={newMapping.access}
                                onChange={(e) => setNewMapping({ ...newMapping, access: e.target.value })}
                                placeholder="Access JSON"
                                required
                            />
                            <button
                                type="submit"
                                className="rounded-lg bg-primary-600 text-white px-4 py-2 text-sm font-medium hover:bg-primary-700"
                            >
                                Assign Access
                            </button>
                        </div>
                    </form>

                    {/* Error Message */}
                    {mappingError && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {mappingError}
                        </div>
                    )}

                    {/* Components Table */}
                    {loadingRoleComponents ? (
                        <SkeletonList />
                    ) : roleComponents.length === 0 ? (
                        <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-xl border border-slate-100 border-dashed">
                            No components assigned to this role yet.
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-xl border border-slate-200">
                            <table className="min-w-full text-sm text-left text-slate-700">
                                <thead className="bg-slate-50 text-xs uppercase text-slate-500 font-semibold">
                                    <tr>
                                        <th className="px-4 py-3">Component</th>
                                        <th className="px-4 py-3">Access Config</th>
                                        <th className="px-4 py-3 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {roleComponents.map((comp) => (
                                        <tr key={comp.component_id} className="hover:bg-slate-50/50">
                                            <td className="px-4 py-3 align-top">
                                                <div className="font-semibold text-slate-900">
                                                    {comp.component_name}
                                                </div>
                                                <div className="text-xs text-slate-500 truncate max-w-[200px]">
                                                    {comp.component_description || '—'}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 align-top">
                                                {editingMappingId === comp.component_id ? (
                                                    <div className="space-y-2">
                                                        <textarea
                                                            className="w-full rounded-md border border-slate-300 px-2 py-1 text-xs font-mono"
                                                            rows={2}
                                                            value={editAccessValue}
                                                            onChange={(e) => setEditAccessValue(e.target.value)}
                                                        />
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => handleUpdateMapping(comp.component_name)}
                                                                className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded hover:bg-emerald-200 flex items-center gap-1"
                                                            >
                                                                <Save size={12} /> Save
                                                            </button>
                                                            <button
                                                                onClick={() => setEditingMappingId(null)}
                                                                className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded hover:bg-slate-200"
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <code className="text-xs bg-slate-100 px-2 py-1 rounded border border-slate-200 block text-slate-600 font-mono break-all max-w-[300px]">
                                                        {typeof comp.access === 'string'
                                                            ? comp.access
                                                            : JSON.stringify(comp.access || {})}
                                                    </code>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-right align-top">
                                                <div className="flex justify-end gap-2">
                                                    {editingMappingId !== comp.component_id && (
                                                        <>
                                                            <Tooltip label="Edit Access">
                                                                <button
                                                                    onClick={() => startEditing(comp)}
                                                                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                                >
                                                                    <Edit2 size={16} />
                                                                </button>
                                                            </Tooltip>
                                                            <Tooltip label="Remove Mapping">
                                                                <button
                                                                    onClick={() => handleDeleteMapping(comp.component_name)}
                                                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                                >
                                                                    <Trash2 size={16} />
                                                                </button>
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default RolesPage;
